'use client';

import { useCallback, useEffect, useRef } from 'react';

import { CommonUtils } from '../../(common-lib)/CommonUtils';
import { MarketingAttributer } from '../../(common-lib)/MarketingAttributer';
import { ExitStrategy, State } from '../../(common-lib)/LoanData';
import { ApiCache } from '../../(ui-lib)/ApiCache';
import { useForm } from '../context/FormContext';
import { useLoading } from '../context/LoadingContext';
import { ScreenControllerFactory } from '../controller/ScreenControllerFactory';
import { PrequalAnalytics } from '../ui-lib/PrequalAnalytics';
import { ScreenOptionsService } from '../ui-lib/ScreenOptionsService';
import { USER_TYPES } from '../../(common-lib)/LoanData';
import { PROPERTY_TYPES } from '../../(common-lib)/PickList';

let isInitialized = false;

export function PreQualPageContent() {
  const { loanData, currentStep, formId, updateFormData, setCurrentStep, setValuesMigrated } = useForm();
  const { isLoading, setLoadingStatus,  loadingTextDefault } = useLoading();
  const hasAppliedPropertyStateParam = useRef(false);
  const hasAppliedTestcaseParam = useRef(false);
  const hasTrackedLanding = useRef(false);
  const controller = ScreenControllerFactory.getController(loanData.userType, loanData.propertyType || PROPERTY_TYPES.SINGLE_FAMILY);
  const CurrentScreen = controller.getCurrentScreen(currentStep);

  const normalizeParamToken = useCallback((value: string): string => {
    return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  }, []);

  const getPrequalQueryParams = useCallback(() => {
    // Supported prequal URL params:
    // - broker=true
    // - propertyState=<US state code or name> (example: CA or California)
    // - exitStrategy=Fix
    const params = new URLSearchParams(window.location.search);
    return {
      propertyStateParam: (params.get('propertyState') || '').trim(),
      exitStrategyParam: (params.get('exitStrategy') || '').trim(),
      brokerParam: (params.get('broker') || '').trim(),
      testcaseParam: (params.get('testcase') || '').trim(),
    };
  }, []);

  const resolvePropertyStateFromParam = useCallback((rawValue: string, stateOptions: State[]): State | undefined => {
    const normalizedValue = rawValue.trim().toUpperCase();
    if (!normalizedValue) {
      return undefined;
    }

    return stateOptions.find((state) =>
      state.key.trim().toUpperCase() === normalizedValue ||
      state.code.trim().toUpperCase() === normalizedValue ||
      state.display.trim().toUpperCase() === normalizedValue
    );
  }, []);

  const resolveExitStrategyFromParam = useCallback((rawValue: string): ExitStrategy | undefined => {
    const normalizedValue = normalizeParamToken(rawValue);
    if (!normalizedValue) {
      return undefined;
    }

    // Expected value from URL is `exitStrategy=Fix`.
    // Internal strategy key for Renovation / Fix & Flip is `FIX`.
    if (normalizedValue !== 'FIX') {
      return undefined;
    }

    const strategyOptions = ScreenOptionsService.getExitStrategyOptions(loanData);
    const matchedStrategy = strategyOptions.find((strategy) => {
      const key = normalizeParamToken(strategy.key);
      const code = normalizeParamToken(strategy.code);
      const display = normalizeParamToken(strategy.display);
      const commercial = normalizeParamToken(strategy.commercial || '');

      return (
        key === normalizedValue ||
        code === normalizedValue ||
        display === normalizedValue ||
        commercial === normalizedValue
      );
    });

    if (matchedStrategy) {
      return matchedStrategy;
    }

    return undefined;
  }, [loanData, normalizeParamToken]);
  const lastCapturedLoanIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (hasTrackedLanding.current) return;
    hasTrackedLanding.current = true;

    PrequalAnalytics.trackLanded(loanData);
  }, [loanData]);

  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    const uniqueLoanId = loanData.crmData?.uniqueLoanId;
    if (!uniqueLoanId) return;
    if (lastCapturedLoanIdRef.current === uniqueLoanId) return;

    lastCapturedLoanIdRef.current = uniqueLoanId;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const captureMarketingAttributes = () => {
      if (cancelled) return;
      attempts += 1;

      if (attempts >= 8) {
        MarketingAttributer.logMappedMarketingAttributes('Prequal landing marketing attributes captured');
        return;
      }

      timeoutId = setTimeout(captureMarketingAttributes, 500);
    };

    captureMarketingAttributes();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loanData, loanData.crmData?.uniqueLoanId, updateFormData]);

  useEffect(() => {

    const initializeFormData = async () => {
      if (isInitialized) return;
      isInitialized = true;
      if (!formId) return;
  
      setLoadingStatus(true);
      loadingTextDefault();
  
      try {
        const enableSalesforce = CommonUtils.getSalesforceEnabledString();
        const formIdParam = encodeURIComponent(formId);
        const url = `/api/crm/form_data?${enableSalesforce}&formid=${formIdParam}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const responseData = await response.json();
        const data = responseData.loanData;
        const migrated = responseData.migrated || false;
        data.userType = USER_TYPES.LOAN_OFFICER;
        
        // Set migration flag in context, NOT in formData
        setValuesMigrated(migrated);
        
        if (data.propertyType?.key === PROPERTY_TYPES.COMMERCIAL.key) {
          alert('Rates and terms are not supported for commercial loans.');
          return;
        }
        updateFormData(data);          
      } catch (error) {
        CommonUtils.logError('Error fetching form data:', error);
        alert('Error fetching form data.');
      } finally {
        setLoadingStatus(false);
      }  
    }

    initializeFormData();

  }, [formId, isLoading, updateFormData, setLoadingStatus, loadingTextDefault, setValuesMigrated]);

  useEffect(() => {
    const applyTestcaseFromUrl = async () => {
      if (hasAppliedTestcaseParam.current || formId) {
        return;
      }

      const { testcaseParam } = getPrequalQueryParams();
      if (!testcaseParam) {
        hasAppliedTestcaseParam.current = true;
        return;
      }

      if (process.env.NODE_ENV === 'production') {
        hasAppliedTestcaseParam.current = true;
        return;
      }

      try {
        const response = await fetch(`/api/dev/pricing-testcase?testcase=${encodeURIComponent(testcaseParam)}`);
        if (!response.ok) {
          throw new Error(`Unable to fetch testcase "${testcaseParam}"`);
        }

        const responseData = await response.json();
        if (responseData?.loanData) {
          updateFormData({
            ...responseData.loanData,
            result: {},
          });
        }
      } catch (error) {
        CommonUtils.logWarn('Unable to apply testcase from URL param', error);
      } finally {
        hasAppliedTestcaseParam.current = true;
      }
    };

    applyTestcaseFromUrl();
  }, [formId, getPrequalQueryParams, updateFormData]);

  useEffect(() => {
    const applyPropertyStateFromUrl = async () => {
      if (hasAppliedPropertyStateParam.current || loanData.propertyState) {
        return;
      }

      const { propertyStateParam } = getPrequalQueryParams();
      if (!propertyStateParam) {
        hasAppliedPropertyStateParam.current = true;
        return;
      }

      try {
        const stateOptions = await ApiCache.getStates();
        const resolvedState = resolvePropertyStateFromParam(propertyStateParam, stateOptions);
        if (resolvedState) {
          updateFormData({ propertyState: resolvedState });
        }
      } catch (error) {
        CommonUtils.logWarn('Unable to apply property state from URL param', error);
      } finally {
        hasAppliedPropertyStateParam.current = true;
      }
    };

    applyPropertyStateFromUrl();
  }, [getPrequalQueryParams, loanData.propertyState, resolvePropertyStateFromParam, updateFormData]);

  useEffect(() => {
    const applyExitStrategyFromUrl = () => {
      if (loanData.exitStrategy) {
        return;
      }

      const { exitStrategyParam } = getPrequalQueryParams();
      if (!exitStrategyParam) {
        return;
      }

      const resolvedExitStrategy = resolveExitStrategyFromParam(exitStrategyParam);
      if (resolvedExitStrategy) {
        updateFormData({ exitStrategy: resolvedExitStrategy });
      }
    };

    applyExitStrategyFromUrl();
  }, [getPrequalQueryParams, loanData.exitStrategy, loanData.propertyType, resolveExitStrategyFromParam, updateFormData]);

  useEffect(() => {
    const applyBrokerFromUrl = () => {
      if (loanData.userType === USER_TYPES.BROKER) {
        return;
      }

      const { brokerParam } = getPrequalQueryParams();
      if (!brokerParam) {
        return;
      }

      if (brokerParam.trim().toLowerCase() === 'true') {
        updateFormData({ userType: USER_TYPES.BROKER });
        // Broker links skip the choice screen and continue through the broker flow.
        if (currentStep === 'Role Selection') {
          setCurrentStep('Broker Info');
        }
      }
    };

    applyBrokerFromUrl();
  }, [currentStep, getPrequalQueryParams, loanData.userType, setCurrentStep, updateFormData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <CurrentScreen />
    </div>
  );
}
